import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { cn } from "../lib/utils";
import { 
  Users, 
  DollarSign, 
  Clock, 
  TrendingUp,
  Search,
  Palette,
  Briefcase,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";

const StaffPnlDetailPage = () => {
  const { formatAmount } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return new Date().toISOString().slice(0, 7);
  });

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/stats/staff-pnl?month=${selectedMonth}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching staff PnL:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => formatAmount(amount, "USD");

  const formatMonth = (monthStr) => {
    // Parse month string directly to avoid timezone issues
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[month - 1]} ${year}`;
  };

  const changeMonth = (direction) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
  };

  const getPositionBadge = (position) => {
    const colors = {
      project_manager: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      art_director: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      "3d_artist": "bg-orange-500/10 text-orange-400 border-orange-500/20",
      animator: "bg-pink-500/10 text-pink-400 border-pink-500/20",
      designer: "bg-green-500/10 text-green-400 border-green-500/20",
      developer: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
      sales: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      admin: "bg-red-500/10 text-red-400 border-red-500/20",
    };
    return colors[position] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20";
  };

  const filteredStaff = data?.staff?.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.position?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-pnl-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <Users className="h-6 w-6 text-orange-500" />
            Staff P&L Details
          </h1>
          <p className="text-sm text-zinc-500">
            Individual employee earnings and payments
          </p>
        </div>
        
        {/* Month Selector */}
        <div className="flex items-center gap-2 bg-zinc-900 rounded-lg p-1">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => changeMonth(-1)}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-zinc-50 font-medium px-3 min-w-[140px] text-center">
            {formatMonth(selectedMonth)}
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => changeMonth(1)}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Base Salaries</span>
              <Briefcase className="h-4 w-4 text-zinc-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-50">
              {formatCurrency(data?.totals?.base_salary)}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-900/20 border-orange-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-orange-400 uppercase font-mono">Salary Pending</span>
              <Clock className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-400">
              {formatCurrency(data?.totals?.salary_pending)}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-yellow-900/20 border-yellow-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-yellow-400 uppercase font-mono">Sales Bonuses</span>
              <TrendingUp className="h-4 w-4 text-yellow-500" />
            </div>
            <p className="text-sm text-zinc-500">Expected: <span className="text-yellow-400 font-bold">{formatCurrency(data?.totals?.sales_bonus_expected || 0)}</span></p>
            <p className="text-sm text-zinc-500">Real: <span className="text-yellow-500 font-bold">{formatCurrency(data?.totals?.sales_bonus_real || 0)}</span></p>
            <p className="text-xs text-zinc-600 mt-1">
              Override Exp: {formatCurrency(data?.totals?.override_expected || 0)} | Real: {formatCurrency(data?.totals?.override_real || 0)}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-900/30 border-purple-800/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-purple-400 uppercase font-mono">AD Fees</span>
              <Palette className="h-4 w-4 text-purple-400" />
            </div>
            <p className="text-xl font-bold text-purple-400">
              {formatCurrency((data?.totals?.ad_fee_paid || 0) + (data?.totals?.ad_fee_pending || 0))}
            </p>
            <p className="text-xs text-zinc-500 mt-1">30% of approved tasks</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-900/20 border-green-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-green-400 uppercase font-mono">Total Earnings</span>
              <DollarSign className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400">
              {formatCurrency((data?.totals?.total_paid || 0) + (data?.totals?.total_pending || 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search staff..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-50"
          />
        </div>
        <span className="text-sm text-zinc-500">
          {filteredStaff.length} employees
        </span>
      </div>

      {/* Staff Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-zinc-50">
            Employee Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Employee</TableHead>
                <TableHead className="text-zinc-400">Position</TableHead>
                <TableHead className="text-right text-zinc-400">Base Salary</TableHead>
                <TableHead className="text-right text-green-400">Salary Paid</TableHead>
                <TableHead className="text-right text-orange-400">Salary Pending</TableHead>
                <TableHead className="text-right text-purple-400">AD Fee</TableHead>
                <TableHead className="text-right text-pink-400">CRM Bonus</TableHead>
                <TableHead className="text-right text-blue-400">PM Bonus</TableHead>
                <TableHead className="text-right text-yellow-400">Sales Expected</TableHead>
                <TableHead className="text-right text-yellow-500">Sales Real</TableHead>
                <TableHead className="text-right text-cyan-400">Override Exp.</TableHead>
                <TableHead className="text-right text-cyan-500">Override Real</TableHead>
                <TableHead className="text-right text-zinc-50 font-medium">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStaff.map((staff) => {
                const totalEarnings = staff.salary_paid + staff.salary_pending + staff.artist_paid + staff.artist_pending + 
                  staff.ad_fee_paid + staff.ad_fee_pending + (staff.sales_bonus_real || 0) + (staff.override_real || 0);
                return (
                <TableRow key={staff.employee_id} className="border-zinc-800">
                  <TableCell className="font-medium text-zinc-50">
                    {staff.name}
                    {staff.task_count > 0 && (
                      <span className="ml-2 text-xs text-zinc-500">
                        ({staff.task_count} tasks)
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("text-xs capitalize", getPositionBadge(staff.position))}>
                      {staff.position?.replace("_", " ") || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-zinc-400">
                    {formatCurrency(staff.base_salary)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-green-400">
                    {staff.salary_paid > 0 ? formatCurrency(staff.salary_paid) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-orange-400">
                    {staff.salary_pending > 0 ? formatCurrency(staff.salary_pending) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-purple-400">
                    {(staff.ad_fee_paid + staff.ad_fee_pending) > 0 
                      ? formatCurrency(staff.ad_fee_paid + staff.ad_fee_pending) 
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-pink-400">
                    {(staff.crm_bonus || 0) > 0 
                      ? formatCurrency(staff.crm_bonus) 
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-blue-400">
                    {(staff.pm_bonus || 0) > 0 
                      ? formatCurrency(staff.pm_bonus) 
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-yellow-400">
                    {(staff.sales_bonus_expected || 0) > 0 
                      ? formatCurrency(staff.sales_bonus_expected) 
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-yellow-500">
                    {(staff.sales_bonus_real || 0) > 0 
                      ? formatCurrency(staff.sales_bonus_real) 
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-cyan-400">
                    {(staff.override_expected || 0) > 0 ? formatCurrency(staff.override_expected) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-cyan-500">
                    {(staff.override_real || 0) > 0 ? formatCurrency(staff.override_real) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-zinc-50 font-medium">
                    {totalEarnings > 0 ? formatCurrency(totalEarnings) : "—"}
                  </TableCell>
                </TableRow>
              )})}
              
              {/* Totals Row */}
              <TableRow className="border-zinc-700 bg-zinc-800/30 font-medium">
                <TableCell className="text-zinc-50">TOTAL</TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right font-mono text-zinc-300">
                  {formatCurrency(data?.totals?.base_salary)}
                </TableCell>
                <TableCell className="text-right font-mono text-green-400">
                  {formatCurrency(data?.totals?.salary_paid)}
                </TableCell>
                <TableCell className="text-right font-mono text-orange-400">
                  {formatCurrency(data?.totals?.salary_pending)}
                </TableCell>
                <TableCell className="text-right font-mono text-purple-400">
                  {formatCurrency((data?.totals?.ad_fee_paid || 0) + (data?.totals?.ad_fee_pending || 0))}
                </TableCell>
                <TableCell className="text-right font-mono text-pink-400">
                  {formatCurrency(data?.totals?.crm_bonus || 0)}
                </TableCell>
                <TableCell className="text-right font-mono text-blue-400">
                  {formatCurrency(data?.totals?.pm_bonus || 0)}
                </TableCell>
                <TableCell className="text-right font-mono text-yellow-400">
                  {formatCurrency(data?.totals?.sales_bonus_expected || 0)}
                </TableCell>
                <TableCell className="text-right font-mono text-yellow-500">
                  {formatCurrency(data?.totals?.sales_bonus_real || 0)}
                </TableCell>
                <TableCell className="text-right font-mono text-cyan-400">
                  {formatCurrency(data?.totals?.override_expected || 0)}
                </TableCell>
                <TableCell className="text-right font-mono text-cyan-500">
                  {formatCurrency(data?.totals?.override_real || 0)}
                </TableCell>
                <TableCell className="text-right font-mono text-zinc-50">
                  {formatCurrency((data?.totals?.total_paid || 0) + (data?.totals?.total_pending || 0))}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Exchange Rates Info */}
      <div className="text-xs text-zinc-600 text-center">
        Exchange rates: 1 USD = {data?.exchange_rates?.usd_to_uah?.toFixed(2)} UAH | 
        1 EUR = {data?.exchange_rates?.eur_to_uah?.toFixed(2)} UAH
      </div>
    </div>
  );
};

export default StaffPnlDetailPage;
